import { ChangeDetectionStrategy, Component, EventEmitter, Inject, Input, Output } from '@angular/core';
import { FormControl, FormGroup, Validators } from '@angular/forms';
import { emailValidator } from '@vendasta/forms';
import { SnackbarService } from '@vendasta/galaxy/snackbar-service';
import { BehaviorSubject, Observable, take } from 'rxjs';
import { UsersServiceInterface, User, UserOptions, UsersServiceInterfaceToken } from '../../users-service-interface';
import { catchError } from 'rxjs/operators';

@Component({
  selector: 'users-create-user-form',
  templateUrl: './create-user-form.component.html',
  styleUrls: ['./create-user-form.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: false,
})
export class CreateUserFormComponent {
  @Input() accountGroupId = '';
  @Input() partnerId = '';
  @Output() userCreated: EventEmitter<User> = new EventEmitter();

  private isCreatingUser$$: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);
  private isCreatingUser$: Observable<boolean> = this.isCreatingUser$$.asObservable();

  readonly form = new FormGroup({
    first_name: new FormControl(''),
    last_name: new FormControl(''),
    user_email: new FormControl('', [Validators.required, emailValidator]),
    phone: new FormControl(''),
  });

  constructor(
    private snackbarService: SnackbarService,
    @Inject(UsersServiceInterfaceToken) private userService: UsersServiceInterface,
  ) {}

  public isCreatingUser(): Observable<boolean> {
    return this.isCreatingUser$;
  }

  createUser(): void {
    if (this.form.invalid) {
      this.form.markAsDirty();
      this.snackbarService.openErrorSnack('CREATE_USER.INVALID_FORM');
      return;
    }

    this.isCreatingUser$$.next(true);

    const user = {
      email: this.form.value.user_email,
      firstName: this.form.value.first_name || '',
      lastName: this.form.value.last_name || '',
    } as User;

    const userOptions = {
      partnerId: this.partnerId,
      accountGroupId: this.accountGroupId,
      sendWelcomeEmail: false,
      phone: this.form.value.phone,
    } as UserOptions;

    this.userService
      .createSMBUser(user, userOptions)
      .pipe(
        take(1),
        catchError((err) => {
          let errTranslationId = 'CREATE_USER.ERROR_SNACK';
          if (err?.data?.message?.includes('LimitError:')) {
            errTranslationId = 'CREATE_USER.RATE_LIMIT_SNACK';
          }
          this.snackbarService.openErrorSnack(errTranslationId);
          this.isCreatingUser$$.next(false);
          throw err;
        }),
      )
      .subscribe((responseUser) => {
        this.userCreated.emit(responseUser);
        this.snackbarService.openSuccessSnack('CREATE_USER.CREATE_USER_SUCCESS');
        this.isCreatingUser$$.next(false);
      });
  }
}
